// src/components/common/ToastContainer.tsx
import {useEffect} from "react";
import {Transition} from "@headlessui/react";
import {X, CheckCircle, XCircle, AlertTriangle, Info} from "lucide-react";
import {useToast} from "../../hooks/common/useToast";
import {clsx} from "clsx";

const ToastContainer: React.FC = () => {
  const {toasts, remove} = useToast();

  useEffect(() => {
    toasts.forEach((toast) => {
      if (toast.duration && toast.duration > 0) {
        const timer = setTimeout(() => {
          remove(toast.id);
        }, toast.duration);

        return () => clearTimeout(timer);
      }
    });
  }, [toasts, remove]);

  const getToastIcon = (type: string) => {
    switch (type) {
      case "success":
        return (
          <CheckCircle className="w-6 h-6 text-success-500 flex-shrink-0" />
        );
      case "error":
        return <XCircle className="w-6 h-6 text-danger-500 flex-shrink-0" />;
      case "warning":
        return (
          <AlertTriangle className="w-6 h-6 text-warning-500 flex-shrink-0" />
        );
      case "info":
        return <Info className="w-6 h-6 text-blue-500 flex-shrink-0" />;
      default:
        return <Info className="w-6 h-6 text-gray-500 flex-shrink-0" />;
    }
  };

  const getToastStyles = (type: string) => {
    switch (type) {
      case "success":
        return "border-l-success-500 bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800";
      case "error":
        return "border-l-danger-500 bg-danger-50 dark:bg-danger-900/20 border-danger-200 dark:border-danger-800";
      case "warning":
        return "border-l-warning-500 bg-warning-50 dark:bg-warning-900/20 border-warning-200 dark:border-warning-800";
      case "info":
        return "border-l-blue-500 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800";
      default:
        return "border-l-gray-500 bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800";
    }
  };

  const getProgressBarColor = (type: string) => {
    switch (type) {
      case "success":
        return "bg-success-500";
      case "error":
        return "bg-danger-500";
      case "warning":
        return "bg-warning-500";
      case "info":
        return "bg-blue-500";
      default:
        return "bg-gray-500";
    }
  };

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[9999] space-y-3 max-w-md w-full">
      {toasts.map((toast) => (
        <Transition
          key={toast.id}
          show={true}
          enter="transform ease-out duration-300 transition"
          enterFrom="translate-y-2 opacity-0 scale-95 sm:translate-y-0 sm:translate-x-2"
          enterTo="translate-y-0 opacity-100 scale-100 sm:translate-x-0"
          leave="transition ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div
            className={clsx(
              "w-full bg-white dark:bg-dark-800 shadow-xl rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 border-l-4 border border-opacity-20 overflow-hidden",
              getToastStyles(toast.type)
            )}
          >
            {/* Progress bar for auto-dismiss toasts */}
            {toast.duration && toast.duration > 0 && (
              <div className="h-1 w-full bg-gray-200 dark:bg-gray-700">
                <div
                  className={clsx(
                    "h-full transition-all ease-linear",
                    getProgressBarColor(toast.type)
                  )}
                  style={{
                    width: "100%",
                    animation: `shrink ${toast.duration}ms linear forwards`,
                  }}
                />
              </div>
            )}

            <div className="p-4">
              <div className="flex items-start space-x-4">
                {/* Icon */}
                <div className="flex-shrink-0 pt-0.5">
                  {getToastIcon(toast.type)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white leading-5">
                    {toast.title}
                  </h4>
                  {toast.message && (
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                      {toast.message}
                    </p>
                  )}
                </div>

                {/* Close button */}
                <div className="flex-shrink-0">
                  <button
                    type="button"
                    className="inline-flex rounded-md p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                    onClick={() => remove(toast.id)}
                  >
                    <span className="sr-only">Dismiss</span>
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      ))}

      {/* CSS Animation */}

      <style>
         {" "}
        {`
          @keyframes shrink {
            from {
              width: 100%;
            }
            to {
              width: 0%;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ToastContainer;
